import React, { useRef, useState } from "react";
import { Box, Button, Alert, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FrontDeskVitalSignsPanel from "./FrontDeskVitalSignsPanel";
import FormModalFooter from "./FormModalFooter";
import {
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../utils/formModalStyles";

const PatientRegistrationSuccessStep = ({
  successMessage,
  showSuccessMessage = false,
  patientId,
  appointmentId,
  portalCredentials = null,
  onRegisterNew,
  onClose,
  onVitalsSaved,
  onPrintInvoice = null,
  hideDismissActions = false,
  enableVitals = true,
}) => {
  const vitalsRef = useRef(null);
  const [vitalsMode, setVitalsMode] = useState("prompt");
  const [vitalsSaving, setVitalsSaving] = useState(false);
  const [vitalsCanSave, setVitalsCanSave] = useState(false);

  const handleRegisterNew = () => {
    vitalsRef.current?.resetForm?.();
    setVitalsMode("prompt");
    onRegisterNew?.();
  };

  const handleClose = () => {
    vitalsRef.current?.resetForm?.();
    setVitalsMode("prompt");
    onClose?.();
  };

  const handleVitalsSaved = () => {
    vitalsRef.current?.resetForm?.();
    setVitalsMode("prompt");
    if (onVitalsSaved) {
      onVitalsSaved();
    } else {
      onClose?.();
    }
  };

  const username = portalCredentials?.username;
  const password = portalCredentials?.password;
  const hasPortalCredentials = !!(username || password);
  const showVitals = enableVitals && !!patientId;

  return (
    <Box sx={{ py: 0 }}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
      </Box>

      {hasPortalCredentials && (
        <Alert
          severity="info"
          sx={{
            py: 0.5,
            mb: 0.75,
            "& .MuiAlert-message": { py: 0.35, fontSize: "0.8125rem", width: "100%" },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
            Patient Portal Login
          </Typography>
          {username && (
            <Typography variant="body2" component="div">
              Username: <strong>{username}</strong>
            </Typography>
          )}
          {password && (
            <Typography variant="body2" component="div">
              Temporary Password: <strong>{password}</strong>
            </Typography>
          )}
        </Alert>
      )}

      {showSuccessMessage && successMessage ? (
        <Alert
          severity="success"
          sx={{ py: 0.25, mb: 0.75, "& .MuiAlert-message": { py: 0.35, fontSize: "0.8125rem" } }}
        >
          {successMessage}
        </Alert>
      ) : null}

      {showVitals ? (
        <FrontDeskVitalSignsPanel
          ref={vitalsRef}
          patientId={patientId}
          appointmentId={appointmentId}
          enabled={!!patientId}
          compact
          hidePromptButtons
          hideFormActions
          mode={vitalsMode}
          onModeChange={setVitalsMode}
          onSaved={handleVitalsSaved}
          onSaveStateChange={({ saving, canSave }) => {
            setVitalsSaving(saving);
            setVitalsCanSave(canSave);
          }}
        />
      ) : null}

      <FormModalFooter
        actionsSx={{
          flexWrap: "nowrap",
          gap: 0.75,
          justifyContent: "center",
        }}
      >
        {showVitals && vitalsMode === "form" ? (
          <>
            {!hideDismissActions && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                sx={FORM_CANCEL_BUTTON_SX}
                disabled={vitalsSaving}
                onClick={() => {
                  vitalsRef.current?.resetForm?.();
                  setVitalsMode("prompt");
                }}
              >
                Back
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={FORM_SUBMIT_BUTTON_SX}
              disabled={vitalsSaving || !vitalsCanSave}
              onClick={() => vitalsRef.current?.save?.()}
            >
              Save
            </Button>
          </>
        ) : showVitals && vitalsMode === "prompt" ? (
          <>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={FORM_SUBMIT_BUTTON_SX}
              disabled={!patientId}
              onClick={() => setVitalsMode("form")}
            >
              Add Vital Signs
            </Button>
            {!hideDismissActions && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                sx={FORM_CANCEL_BUTTON_SX}
                onClick={handleClose}
              >
                Skip
              </Button>
            )}
          </>
        ) : null}

        {typeof onPrintInvoice === "function" ? (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            sx={FORM_CANCEL_BUTTON_SX}
            onClick={onPrintInvoice}
          >
            Print Invoice
          </Button>
        ) : null}

        <Button
          size="small"
          variant="outlined"
          color="primary"
          sx={FORM_CANCEL_BUTTON_SX}
          onClick={handleRegisterNew}
        >
          Register New Patient
        </Button>
      </FormModalFooter>
    </Box>
  );
};

export default PatientRegistrationSuccessStep;
